import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";

//FilterForm  Context
export type initialFilterStateProps = {
  rmNo: string;
  rmOlkCd: string;
  rmTypCd: string;
  bedTypCd: string;
  maxPerCtMax: string;
  maxPerCtMin: string;
  chldFreePerCtMax: string;
  babyFreePerCtMax: string;
  uyn: string;
};
export interface RoomFilterContextType {
  filters: initialFilterStateProps;
  onChange: (name: string, value: string) => void;
  onSelect: (name: string, value: string) => void;
  onReset: () => void;
}

const RoomFilterContext = createContext<RoomFilterContextType | undefined>(
  undefined
);

interface RoomFilterProviderProps {
  children: ReactNode;
}

export const RoomFilterProvider = ({ children }: RoomFilterProviderProps) => {
  const initialFilterState = {
    rmNo: "",
    rmOlkCd: "",
    rmTypCd: "",
    bedTypCd: "",
    maxPerCtMax: "",
    maxPerCtMin: "",
    chldFreePerCtMax: "",
    babyFreePerCtMax: "",
    uyn: "Y",
  };

  const [filters, setFilters] =
    useState<initialFilterStateProps>(initialFilterState);

  const onChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onSelect = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onReset = () => {
    setFilters(initialFilterState);
  };

  return (
    <RoomFilterContext.Provider
      value={{
        filters,
        onChange,
        onSelect,
        onReset,
      }}
    >
      {children}
    </RoomFilterContext.Provider>
  );
};

export const useRoomFilterContext = (): RoomFilterContextType => {
  const context = useContext(RoomFilterContext);

  if (!context) {
    throw new Error(
      "useRoomFilterContext는 RoomFilterProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 리스트 조회 context
interface RoomListContextType {
  isSuccess: boolean;
  setIsSuccess: (isSuccess: boolean) => void;
  param: Partial<initialFilterStateProps> | undefined;
  setParam: React.Dispatch<
    React.SetStateAction<Partial<initialFilterStateProps> | undefined>
  >;
}

const RoomListContext = createContext<RoomListContextType | undefined>(
  undefined
);

interface RoomListProviderProps {
  children: ReactNode;
}

export const RoomListProvider = ({ children }: RoomListProviderProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [param, setParam] = useState<
    Partial<initialFilterStateProps> | undefined
  >(undefined);

  return (
    <RoomListContext.Provider
      value={{ isSuccess, setIsSuccess, param, setParam }}
    >
      {children}
    </RoomListContext.Provider>
  );
};

export const useRoomListContext = (): RoomListContextType => {
  const context = useContext(RoomListContext);

  if (!context) {
    throw new Error(
      "useRoomListContext는 RoomListProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 기본정보 Context 타입 정의
interface RoomBaseContextType {
  isSuccess: boolean;
  setIsSuccess: (isSuccess: boolean) => void;
  rmNo: string;
  setRmNo: (rmNo: string) => void;
}

const RoomBaseContext = createContext<RoomBaseContextType | undefined>(
  undefined
);

interface RoomBaseProviderProps {
  children: ReactNode;
}

export const RoomBaseProvider = ({ children }: RoomBaseProviderProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [rmNo, setRmNo] = useState("");

  return (
    <RoomBaseContext.Provider
      value={{ isSuccess, setIsSuccess, rmNo, setRmNo }}
    >
      {children}
    </RoomBaseContext.Provider>
  );
};

export const useRoomBaseContext = (): RoomBaseContextType => {
  const context = useContext(RoomBaseContext);

  if (!context) {
    throw new Error(
      "useRoomBaseContext는 RoomBaseProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 추가정보 Context
interface RoomAddInfContextType {
  isSuccess: boolean;
  setIsSuccess: (isSuccess: boolean) => void;
  koValuesRef: React.MutableRefObject<null>;
  enValuesRef: React.MutableRefObject<null>;
  jaValuesRef: React.MutableRefObject<null>;
  getDefaultValue: (langCd: string) => any;
  resetDefaultValue: (langCd: string) => void;
}

const RoomAddInfContext = createContext<RoomAddInfContextType | undefined>(
  undefined
);

interface RoomAddInfProviderProps {
  children: ReactNode;
}

export const RoomAddInfProvider = ({ children }: RoomAddInfProviderProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const koValuesRef = useRef(null);
  const enValuesRef = useRef(null);
  const jaValuesRef = useRef(null);

  const getDefaultValue = (langCd: string) => {
    return langCd === "ko"
      ? koValuesRef.current
      : langCd === "en"
      ? enValuesRef.current
      : langCd === "ja"
      ? jaValuesRef.current
      : undefined;
  };

  const resetDefaultValue = (langCd: string) => {
    if (langCd === "ko") {
      koValuesRef.current = null;
    }
    if (langCd === "en") {
      enValuesRef.current = null;
    }
  };

  return (
    <RoomAddInfContext.Provider
      value={{
        isSuccess,
        setIsSuccess,
        koValuesRef,
        enValuesRef,
        jaValuesRef,
        getDefaultValue,
        resetDefaultValue,
      }}
    >
      {children}
    </RoomAddInfContext.Provider>
  );
};

export const useRoomAddInfContext = (): RoomAddInfContextType => {
  const context = useContext(RoomAddInfContext);

  if (!context) {
    throw new Error(
      "useRoomAddInfContext는 RoomAddInfProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 이미지 Context
interface RoomImageContextType {
  isOpenImgUpdate: boolean;
  setIsOpenImgUpdate: (isOpenImgUpdate: boolean) => void;
  isSuccess: boolean;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
}

const RoomImageContext = createContext<RoomImageContextType | undefined>(
  undefined
);

interface RoomImageProviderProps {
  children: ReactNode;
}

export const RoomImageProvider = ({ children }: RoomImageProviderProps) => {
  const [isOpenImgUpdate, setIsOpenImgUpdate] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  return (
    <RoomImageContext.Provider
      value={{
        isOpenImgUpdate,
        setIsOpenImgUpdate,
        isSuccess,
        setIsSuccess,
      }}
    >
      {children}
    </RoomImageContext.Provider>
  );
};

export const useRoomImageContext = (): RoomImageContextType => {
  const context = useContext(RoomImageContext);

  if (!context) {
    throw new Error(
      "useRoomImageContext는 RoomImageProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};
